import i18next from 'i18next';

import { signIn, checkUser, signOut, backFromImpersonate } from '../../api';
import {
  SIGN_IN_START,
  SIGN_OUT_START,
  SIGN_IN_SUCCESS,
  SIGN_IN_ERROR,
  SIGN_OUT_END,
  CLOSE_ACTIVE_SHIPMENT,
  CLEAR_SEARCH,
  CLEAR_ALL_DATA,
  REMOVE_ESCALATIONS,
} from '../../consts/action-types';
import * as datadogUtils from '../../services/datadog-utils';
import * as pendoUtils from '../../services/pendo-utils';
import { AccessToken } from '../../services/access-token';
import { unleashClient } from '../../services/UnleashProvider/const';
import { SharedReduxState } from '../../reducers/types';
import CancellableRequest from '../../services/cancellable-request';
import { PACK } from '../../consts/packs';
import { currentSSOService } from '../../services/sso-auth-service/CurrentSSOService';
import { UserImpersonation } from '../../services/user-impersonation';
import { ForceChangePassword } from '../../services/force-change-password';
import { UserCurrentCompany } from '../../services/user-company';
import { portalLevelPermissions } from '../../modules/Permissions/PortalLevelPermissions';

import { clearAuthData, clearLocalStorage } from './utils';

type Dispatch = (action: any) => void;

export const authSignIn = (values: { email: string; password: string }) => async (dispatch: Dispatch) => {
  dispatch({ type: SIGN_IN_START });
  try {
    const authResponse = await signIn(values);
    ForceChangePassword.setChangePasswordValue(authResponse.force_change_password);
  } catch (err) {
    dispatch({ type: SIGN_IN_ERROR });
    throw new Error(i18next.t('auth.error_invalidCredentials'));
  }
  await dispatch(authValidate());
};

export const authValidate = () => async (dispatch: Dispatch) => {
  if (!AccessToken.getToken()) throw new Error('No token');
  dispatch({ type: SIGN_IN_START });
  currentSSOService.initializeSSOService();
  try {
    const user = await checkUser();
    datadogUtils.setUser(user);
    const userCompanyId = UserCurrentCompany.getCompany()?.toString() || '';

    await unleashClient.updateContext({
      userId: user.id,
      properties: {
        email: user.email,
        ...(userCompanyId && { company_id: userCompanyId })
      }
    });
    dispatch({ type: SIGN_IN_SUCCESS, payload: user });
  } catch (err) {
    dispatch({ type: SIGN_IN_ERROR });
    clearAuthData();
    currentSSOService.setSSOService(null);
    throw new Error('token is invalid');
  }
};

const cancelableRequest = new CancellableRequest();

export const authSignOut = () => {
  return (dispatch: Dispatch, getState: () => SharedReduxState) => {
    cancelableRequest?.cancel();
    dispatch({ type: SIGN_OUT_START });

    const clearUserData = () => {
      clearAuthData();
      clearLocalStorage();
      datadogUtils.removeUser();
    };

    const ssoService = currentSSOService.getSSOService();
    const currentCompany = getState().userCompanies.current!;
    const api = ssoService ? ssoService.getLogoutApi(currentCompany.subdomain) : signOut;

    if (typeof api === 'string') { // If the SSO service returns a string, it is a URL to redirect to
      clearUserData();
      window.location.assign(api);
      return;
    }

    api({
      params: undefined,
      abortSignal: cancelableRequest.abortController?.signal
    });
    clearUserData();
    ForceChangePassword.clearChangePasswordValue();
    portalLevelPermissions.reset();
    dispatch({ type: CLOSE_ACTIVE_SHIPMENT });
    dispatch({ type: CLEAR_SEARCH });
    dispatch({ type: CLEAR_ALL_DATA });
    dispatch({ type: REMOVE_ESCALATIONS });
    dispatch({ type: SIGN_OUT_END });
    window.history.pushState(null, '');
    currentSSOService.setSSOService(null);
    unleashClient.updateContext({ userId: undefined });
    window.location.pathname = PACK.rootApp;
  };
};

export const getBackFromImpersonate = () => async (dispatch: Dispatch) => {
  try {
    await backFromImpersonate();
    UserImpersonation.clearImpersonationData();
    UserCurrentCompany.clearCompany();
    window.location.reload();
  } catch (err) {
    clearAuthData();
    dispatch({ type: SIGN_IN_ERROR });
  }
};

export const initializePendoUser = () => (_: any, getState: () => SharedReduxState) => {
  const { auth, userPreferences, userCompanies } = getState();
  const params = {
    user: auth.user!,
    company: userCompanies.current!,
    userPreferences
  };
  pendoUtils.setUser(params);
};
